import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../Helpers/Handler";

const APPLIES_TO_OPTIONS = [
    { key: "Rent", label: "Rent" },
    { key: "SD", label: "Security deposit" },
    { key: "DP", label: "Delivery / pickup fee" },
];

const emptyForm = () => ({
    couponCode: "",
    name: "",
    description: "",
    campaign: "",
    discountType: "percentage",
    discountValue: "",
    maxDiscount: "",
    appliesTo: ["Rent"],
    audience: "all",
    usageLimit: "",
    perCustomerLimit: "1",
    stackableWithCoins: false,
    startAt: "",
    expireAt: "",
    newCustomerOnly: false,
    minCompletedBookings: "",
    maxCompletedBookings: "",
    maxDamageIncidents: "",
    minBookingValue: "",
    maxBookingValue: "",
});

const toDateInput = (value) => (value ? new Date(value).toISOString().slice(0, 10) : "");

const CouponFormModal = ({ coupon, onClose, onSaved }) => {
    const modalRef = useRef(null);
    const bsModalRef = useRef(null);
    const mode = coupon?.couponCode ? "edit" : "create";
    const [showEligibility, setShowEligibility] = useState(false);
    const [form, setForm] = useState(coupon ? {
        couponCode: coupon.couponCode || "",
        name: coupon.name || "",
        description: coupon.description || "",
        campaign: coupon.campaign || "",
        discountType: coupon.discountType || "percentage",
        discountValue: coupon.discountValue ?? "",
        maxDiscount: coupon.maxDiscount ?? "",
        appliesTo: coupon.appliesTo || ["Rent"],
        audience: coupon.audience || "all",
        usageLimit: coupon.usageLimit ?? "",
        perCustomerLimit: coupon.perCustomerLimit ?? "1",
        stackableWithCoins: !!coupon.stackableWithCoins,
        startAt: toDateInput(coupon.startAt),
        expireAt: toDateInput(coupon.expireAt),
        newCustomerOnly: !!coupon.eligibility?.newCustomerOnly,
        minCompletedBookings: coupon.eligibility?.minCompletedBookings ?? "",
        maxCompletedBookings: coupon.eligibility?.maxCompletedBookings ?? "",
        maxDamageIncidents: coupon.eligibility?.maxDamageIncidents ?? "",
        minBookingValue: coupon.eligibility?.minBookingValue ?? "",
        maxBookingValue: coupon.eligibility?.maxBookingValue ?? "",
    } : emptyForm());
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const el = modalRef.current;
        if (!el || !window.bootstrap) return undefined;
        const instance = window.bootstrap.Modal.getOrCreateInstance(el);
        bsModalRef.current = instance;
        const handleHidden = () => onClose?.();
        el.addEventListener("hidden.bs.modal", handleHidden);
        instance.show();
        return () => { el.removeEventListener("hidden.bs.modal", handleHidden); instance.hide(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

    const toggleAppliesTo = (key) => {
        setForm((prev) => {
            const has = prev.appliesTo.includes(key);
            const next = has ? prev.appliesTo.filter((v) => v !== key) : [...prev.appliesTo, key];
            return { ...prev, appliesTo: next };
        });
    };

    const onSubmit = async () => {
        if (mode === "create" && !form.couponCode.trim()) { toast.error("Coupon code is required"); return; }
        if (!form.name.trim()) { toast.error("Name is required"); return; }
        if (!form.description.trim()) { toast.error("Description is required"); return; }
        if (form.discountValue === "" || Number.isNaN(Number(form.discountValue)) || Number(form.discountValue) < 0) {
            toast.error("Discount value is required"); return;
        }
        if (form.discountType === "percentage") {
            if (Number(form.discountValue) > 100) { toast.error("A percentage discount can't exceed 100"); return; }
            if (form.maxDiscount === "" || Number.isNaN(Number(form.maxDiscount)) || Number(form.maxDiscount) < 0) {
                toast.error("A percentage coupon needs a maximum discount cap"); return;
            }
        }
        if (!form.appliesTo.length) { toast.error("Pick at least one thing this coupon applies to"); return; }
        if (form.startAt && form.expireAt && new Date(form.startAt) >= new Date(form.expireAt)) {
            toast.error("Start date must be before expiry date"); return;
        }

        const eligibility = {
            newCustomerOnly: form.newCustomerOnly,
            ...(form.minCompletedBookings !== "" ? { minCompletedBookings: Number(form.minCompletedBookings) } : {}),
            ...(form.maxCompletedBookings !== "" ? { maxCompletedBookings: Number(form.maxCompletedBookings) } : {}),
            ...(form.maxDamageIncidents !== "" ? { maxDamageIncidents: Number(form.maxDamageIncidents) } : {}),
            ...(form.minBookingValue !== "" ? { minBookingValue: Number(form.minBookingValue) } : {}),
            ...(form.maxBookingValue !== "" ? { maxBookingValue: Number(form.maxBookingValue) } : {}),
        };

        const payload = {
            ...(mode === "create" ? { couponCode: form.couponCode.trim().toUpperCase() } : {}),
            name: form.name.trim(),
            description: form.description.trim(),
            ...(form.campaign.trim() ? { campaign: form.campaign.trim() } : {}),
            discountType: form.discountType,
            discountValue: Number(form.discountValue),
            ...(form.discountType === "percentage" ? { maxDiscount: Number(form.maxDiscount) } : {}),
            appliesTo: form.appliesTo,
            audience: form.audience,
            eligibility,
            ...(form.usageLimit !== "" ? { usageLimit: Number(form.usageLimit) } : {}),
            ...(form.perCustomerLimit !== "" ? { perCustomerLimit: Number(form.perCustomerLimit) } : {}),
            stackableWithCoins: form.stackableWithCoins,
            ...(form.startAt ? { startAt: form.startAt } : {}),
            ...(form.expireAt ? { expireAt: form.expireAt } : {}),
        };

        setSaving(true);
        const response = mode === "create"
            ? await Handler({ method: "post", url: "/admin/coupon", data: payload })
            : await Handler({ method: "patch", url: `/admin/coupon/${form.couponCode}`, data: payload });
        setSaving(false);
        if (response?.success) {
            toast.success(response?.data?.message || "Saved successfully");
            onSaved?.();
        } else {
            toast.error(response?.data?.message || "Unable to save this coupon");
        }
    };

    return (
        <div className="modal fade" tabIndex="-1" ref={modalRef} aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content border-0 rounded-4">
                    <div className="modal-header">
                        <h5 className="modal-title fw-semibold">{mode === "create" ? "New coupon" : "Edit coupon"}</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div className="row g-2">
                            <div className="col-md-6">
                                <label className="form-label small text-muted mb-1">Coupon code</label>
                                <input
                                    type="text"
                                    className="form-control text-uppercase"
                                    value={form.couponCode}
                                    disabled={mode === "edit"}
                                    onChange={(e) => set("couponCode", e.target.value.toUpperCase())}
                                    placeholder="e.g. WELCOME10"
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label small text-muted mb-1">Name</label>
                                <input type="text" className="form-control" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Shown to customers" />
                            </div>
                            <div className="col-12">
                                <label className="form-label small text-muted mb-1">Description</label>
                                <textarea className="form-control" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label small text-muted mb-1">Campaign (optional)</label>
                                <input type="text" className="form-control" value={form.campaign} onChange={(e) => set("campaign", e.target.value)} placeholder="e.g. Diwali2026" />
                            </div>

                            <div className="col-md-4">
                                <label className="form-label small text-muted mb-1">Discount type</label>
                                <select className="form-select" value={form.discountType} onChange={(e) => set("discountType", e.target.value)}>
                                    <option value="percentage">Percentage</option>
                                    <option value="flat">Flat amount</option>
                                </select>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label small text-muted mb-1">{form.discountType === "percentage" ? "Percentage off" : "Amount off"}</label>
                                <input type="number" min="0" max={form.discountType === "percentage" ? 100 : undefined} step="0.01" className="form-control" value={form.discountValue} onChange={(e) => set("discountValue", e.target.value)} />
                            </div>
                            {form.discountType === "percentage" ? (
                                <div className="col-md-4">
                                    <label className="form-label small text-muted mb-1">Max discount cap</label>
                                    <input type="number" min="0" step="0.01" className="form-control" value={form.maxDiscount} onChange={(e) => set("maxDiscount", e.target.value)} />
                                </div>
                            ) : ""}

                            <div className="col-12">
                                <label className="form-label small text-muted mb-1 d-block">Applies to</label>
                                {APPLIES_TO_OPTIONS.map((opt) => (
                                    <div className="form-check form-check-inline" key={opt.key}>
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id={`applies-${opt.key}`}
                                            checked={form.appliesTo.includes(opt.key)}
                                            onChange={() => toggleAppliesTo(opt.key)}
                                        />
                                        <label className="form-check-label" htmlFor={`applies-${opt.key}`}>{opt.label}</label>
                                    </div>
                                ))}
                            </div>

                            <div className="col-md-4">
                                <label className="form-label small text-muted mb-1">Audience</label>
                                <select className="form-select" value={form.audience} onChange={(e) => set("audience", e.target.value)}>
                                    <option value="all">Everyone (manual code entry)</option>
                                    <option value="targeted">Targeted (granted to specific customers)</option>
                                </select>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label small text-muted mb-1">Overall usage limit (optional)</label>
                                <input type="number" min="1" className="form-control" value={form.usageLimit} onChange={(e) => set("usageLimit", e.target.value)} placeholder="Unlimited" />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label small text-muted mb-1">Per-customer limit</label>
                                <input type="number" min="1" className="form-control" value={form.perCustomerLimit} onChange={(e) => set("perCustomerLimit", e.target.value)} />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label small text-muted mb-1">Start date (optional)</label>
                                <input type="date" className="form-control" value={form.startAt} onChange={(e) => set("startAt", e.target.value)} />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label small text-muted mb-1">Expiry date (optional)</label>
                                <input type="date" className="form-control" value={form.expireAt} onChange={(e) => set("expireAt", e.target.value)} />
                            </div>

                            <div className="col-12">
                                <div className="form-check form-switch">
                                    <input type="checkbox" className="form-check-input" id="stackableWithCoins" checked={form.stackableWithCoins} onChange={(e) => set("stackableWithCoins", e.target.checked)} />
                                    <label className="form-check-label" htmlFor="stackableWithCoins">Can be combined with coin discounts</label>
                                </div>
                            </div>

                            <div className="col-12 mt-2">
                                <button type="button" className="btn btn-sm btn-link text-decoration-none px-0" onClick={() => setShowEligibility((v) => !v)}>
                                    {showEligibility ? "Hide" : "Show"} advanced eligibility rules
                                </button>
                            </div>

                            {showEligibility ? (
                                <div className="col-12">
                                    <div className="row g-2 border rounded-3 p-2 mx-0">
                                        <div className="col-12">
                                            <div className="form-check">
                                                <input type="checkbox" className="form-check-input" id="newCustomerOnly" checked={form.newCustomerOnly} onChange={(e) => set("newCustomerOnly", e.target.checked)} />
                                                <label className="form-check-label" htmlFor="newCustomerOnly">First booking only</label>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label small text-muted mb-1">Min completed bookings</label>
                                            <input type="number" min="0" className="form-control" value={form.minCompletedBookings} onChange={(e) => set("minCompletedBookings", e.target.value)} />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label small text-muted mb-1">Max completed bookings</label>
                                            <input type="number" min="0" className="form-control" value={form.maxCompletedBookings} onChange={(e) => set("maxCompletedBookings", e.target.value)} />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label small text-muted mb-1">Max damage incidents</label>
                                            <input type="number" min="0" className="form-control" value={form.maxDamageIncidents} onChange={(e) => set("maxDamageIncidents", e.target.value)} />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label small text-muted mb-1">Min booking value</label>
                                            <input type="number" min="0" className="form-control" value={form.minBookingValue} onChange={(e) => set("minBookingValue", e.target.value)} />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label small text-muted mb-1">Max booking value</label>
                                            <input type="number" min="0" className="form-control" value={form.maxBookingValue} onChange={(e) => set("maxBookingValue", e.target.value)} />
                                        </div>
                                    </div>
                                </div>
                            ) : ""}
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
                        <button type="button" className="btn btn-primary" onClick={onSubmit} disabled={saving}>{saving ? "Saving..." : "Save"}</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CouponFormModal;
